import { diffKind, DiffKind } from '@common/audit-trail/auditTrailFormat';
import { format } from 'date-fns';
import { EntityAuditTrailDescriptor } from './entityAuditTrail';
import { ClassifierData } from 'types/classifiers';
import { AuditValueType, EntityAuditTrailEntryDto } from 'types/entity-audit-trail';
import { formatTimeOfDay } from 'utils/date';
import { getClassifierText, getIntl, t } from 'utils/getTranslations';
import { isNewDesignCopy } from 'utils/newDesignCopy';

/**
 * The BE serializes changedAt as a UTC LocalDateTime without a zone suffix — same contract as the
 * generic audit trail. Unlike that page (a forensic log rendered in UTC), this trail shows business
 * events in the viewer's local time, with the full UTC instant in a tooltip.
 */
export function parseChangedAt(changedAt: string): Date {
    return new Date(changedAt.endsWith('Z') ? changedAt : changedAt + 'Z');
}

const UTC_INSTANT: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
};

// Classic's formatters, built once, on first use rather than at module load. The new design writes through its intl.
let formatters: { date: Intl.DateTimeFormat; tooltip: Intl.DateTimeFormat } | undefined;

function localeFormatters() {
    if (!formatters) {
        formatters = {
            date: new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
            tooltip: new Intl.DateTimeFormat('en-GB', UTC_INSTANT),
        };
    }
    return formatters;
}

/** A calendar day: in the new design the company's date format once the caller hands it in, else `21 Oct 2026`. */
function formatDay(date: Date, dateFormat?: string): string {
    return isNewDesignCopy() && dateFormat ? format(date, dateFormat) : localeFormatters().date.format(date);
}

function localDayStamp(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function localDateBucketLabel(date: Date, dateFormat?: string): string {
    const day = localDayStamp(date);
    const today = localDayStamp(new Date());
    // setDate(-1), not "now minus 24h": a DST-transition day is not 24 hours long.
    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterday = localDayStamp(yesterdayDate);
    if (day === today) {
        return t('audit-trail.today');
    }
    if (day === yesterday) {
        return t('audit-trail.yesterday');
    }
    return formatDay(date, dateFormat);
}

export function localTimeLabel(date: Date): string {
    return formatTimeOfDay(date);
}

/** The full UTC instant; the new design's locale writes it, the zone's name included. */
export function utcTooltip(date: Date): string {
    return isNewDesignCopy()
        ? getIntl().formatDate(date, { ...UTC_INSTANT, timeZoneName: 'short' })
        : `${localeFormatters().tooltip.format(date)} UTC`;
}

/** One feed card: all lines by one author within one minute (a workflow action, not one save). */
export interface AuditEntryGroup {
    key: string;
    author: string | null;
    authorId: number | null;
    changedAt: Date;
    lines: EntityAuditTrailEntryDto[];
}

export interface AuditDayBucket {
    label: string;
    groups: AuditEntryGroup[];
}

/**
 * Buckets entries (already sorted changedAt DESC by the BE) by local calendar day, and inside a day
 * merges all lines of the same author within the same minute into one card. One workflow action —
 * a bulk finalise-and-send, say — spans several back-to-back transactions, and a card per
 * transaction shredded it into near-identical cards sharing a timestamp; the author+minute key
 * folds them back together, while a save straddling a minute boundary merely splits into two
 * cards. Groups are looked up by key rather than compared against the previous one, so authors
 * whose timestamps interleave still get one card each.
 */
export function bucketByLocalDay(entries: EntityAuditTrailEntryDto[], dateFormat?: string): AuditDayBucket[] {
    const buckets: AuditDayBucket[] = [];
    const groupsByKey = new Map<string, AuditEntryGroup>();
    for (const entry of entries) {
        const changedAt = parseChangedAt(entry.changedAt);
        const label = localDateBucketLabel(changedAt, dateFormat);
        let bucket = buckets[buckets.length - 1];
        if (!bucket || bucket.label !== label) {
            bucket = { label, groups: [] };
            buckets.push(bucket);
        }
        const minute = Math.floor(changedAt.getTime() / 60_000);
        const groupKey = `${label}:${entry.authorId ?? 'system'}:${minute}`;
        const group = groupsByKey.get(groupKey);
        if (group) {
            // Entries arrive newest-first, so prepending renders the card's lines chronologically —
            // a multi-step change ("Draft → Reviewed", then "Reviewed → Approved") reads top-down.
            group.lines.unshift(entry);
        } else {
            const created: AuditEntryGroup = {
                key: groupKey,
                author: entry.author,
                authorId: entry.authorId,
                changedAt,
                lines: [entry],
            };
            groupsByKey.set(groupKey, created);
            bucket.groups.push(created);
        }
    }
    return buckets;
}

function eventCodeToKey(eventCode: string): string {
    return eventCode.toLowerCase().replace(/_/g, '-');
}

/** One run of an event sentence: either fixed wording or an interpolated value, which renders emphasised. */
export interface SentenceSegment {
    text: string;
    value: boolean;
}

// Private-use code points fence each interpolated value inside the translated sentence, so the
// value runs can be told apart from the wording after react-intl has done the interpolation.
const VALUE_START = '\uE000';
const VALUE_END = '\uE001';
const VALUE_RUN = /\uE000([^\uE001]*)\uE001/g;

function fence(value: string): string {
    return `${VALUE_START}${value}${VALUE_END}`;
}

function toSegments(fenced: string): SentenceSegment[] {
    const segments: SentenceSegment[] = [];
    let cursor = 0;
    for (const match of fenced.matchAll(VALUE_RUN)) {
        if (match.index > cursor) {
            segments.push({ text: fenced.slice(cursor, match.index), value: false });
        }
        segments.push({ text: match[1], value: true });
        cursor = match.index + match[0].length;
    }
    if (cursor < fenced.length) {
        segments.push({ text: fenced.slice(cursor), value: false });
    }
    return segments;
}

/**
 * The sentence of one event line as segments. Classifier-coded params are resolved to the viewer's
 * language first (falling back to the code when the classifier is not loaded), and a non-empty
 * `reason` param is appended as its own clause so no sentence has to carry an optional tail.
 * Every interpolated value is a {@link SentenceSegment#value} segment so the drawer can emphasise it.
 */
export function eventSentenceSegments(
    trail: Pick<EntityAuditTrailDescriptor, 'localeNamespace' | 'paramClassifiers'>,
    entry: EntityAuditTrailEntryDto,
    classifiers: Record<string, ClassifierData | undefined>
): SentenceSegment[] {
    const resolved = { ...entry.params };
    for (const [paramName, { classifierCode, as }] of Object.entries(trail.paramClassifiers ?? {})) {
        const code = resolved[paramName];
        if (code !== undefined) {
            resolved[as] = getClassifierText(classifiers[classifierCode], code);
        }
    }
    const fencedParams = Object.fromEntries(Object.entries(resolved).map(([name, value]) => [name, fence(value)]));
    let sentence = t(`${trail.localeNamespace}.events.${eventCodeToKey(entry.eventCode)}`, fencedParams);
    const reason = entry.params.reason;
    if (reason) {
        sentence += ` \u00b7 ${t('audit-trail.reason', { reason: fence(reason) })}`;
    }
    return toSegments(sentence);
}

/** The plain-text form of {@link eventSentenceSegments}. */
export function eventSentence(
    trail: Pick<EntityAuditTrailDescriptor, 'localeNamespace' | 'paramClassifiers'>,
    entry: EntityAuditTrailEntryDto,
    classifiers: Record<string, ClassifierData | undefined>
): string {
    return eventSentenceSegments(trail, entry, classifiers)
        .map((segment) => segment.text)
        .join('');
}

/** The note under a line a contact merge caused, from the merge params the BE adds; null for any other line. */
export function mergeNote(entry: EntityAuditTrailEntryDto): string | null {
    const { mergedAwayName, mergedAwayClientNumber, mergedAwayPartyNumber, keptName } = entry.params;
    if (!mergedAwayName || !keptName) {
        return null;
    }
    // The BE sets at most one number: the one the merged-away contact's page header showed, under its label.
    const number = mergedAwayClientNumber ?? mergedAwayPartyNumber;
    if (!number) {
        return t('contacts.merge.invoice-note-no-number', { mergedAway: mergedAwayName, kept: keptName });
    }
    return t(mergedAwayClientNumber ? 'contacts.merge.invoice-note-client-number' : 'contacts.merge.invoice-note', {
        mergedAway: mergedAwayName,
        number,
        kept: keptName,
    });
}

export function categoryLabel(localeNamespace: string, category: string): string {
    return t(`${localeNamespace}.categories.${eventCodeToKey(category)}`);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The display form of one value side. Entity references and durations arrive pre-humanized from
 * the BE; classifier codes, booleans and dates are resolved here because they are
 * locale-dependent.
 */
export function displayValue(
    entry: EntityAuditTrailEntryDto,
    value: string | null,
    classifiers: Record<string, ClassifierData | undefined>,
    dateFormat?: string
): string {
    if (value === null) {
        return '';
    }
    if (entry.valueType === 'CLASSIFIER' && entry.classifierCode) {
        return getClassifierText(classifiers[entry.classifierCode], value);
    }
    if (entry.valueType === 'BOOLEAN') {
        return value === 'true' ? t('yes') : t('no');
    }
    if (entry.valueType === 'DATE' && ISO_DATE.test(value)) {
        // Parsed as local midnight on purpose: a plain date names a calendar day, not an instant.
        return formatDay(new Date(`${value}T00:00:00`), dateFormat);
    }
    return value;
}

/** The value line of one event: only the sides that exist, so `add`/`remove` render a single value. */
export interface AuditValueChange {
    kind: DiffKind;
    oldValue: string;
    newValue: string;
}

/** Null for verb-only events (opened, postponed, reminder sent) — those render no value line. */
export function valueChange(
    entry: EntityAuditTrailEntryDto,
    classifiers: Record<string, ClassifierData | undefined>,
    dateFormat?: string
): AuditValueChange | null {
    const oldValue = displayValue(entry, entry.oldValue, classifiers, dateFormat);
    const newValue = displayValue(entry, entry.newValue, classifiers, dateFormat);
    if (oldValue === '' && newValue === '') {
        return null;
    }
    return { kind: diffKind(oldValue, newValue), oldValue, newValue };
}

const URL_VALUE = /^https?:\/\/\S+$/;

/** A bare URL value (a document folder link, say) renders as a hyperlink instead of the full address. */
export function isUrlValue(value: string): boolean {
    return URL_VALUE.test(value);
}

const MONOSPACED_VALUE_TYPES: ReadonlySet<AuditValueType> = new Set(['NUMBER', 'DATE', 'DURATION']);

/** Amounts, dates and durations get the mono face so the two sides line up digit for digit. */
export function isMonospacedValue(entry: EntityAuditTrailEntryDto): boolean {
    return MONOSPACED_VALUE_TYPES.has(entry.valueType);
}
